import { Combobox } from '@squareup/dex-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import { toIgnitionDataType } from '@squareup/dex-utils-oas';
import {
  AnyFieldOptions,
  AnyFieldOptionsType,
  customAttributeTypes,
  transformValueToPrimitive,
} from '@squareup/dex-utils-shared-api-explorer';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { ApiExplorerBooleanField } from './primitives/ApiExplorerBooleanField';
import { ApiExplorerJsonField } from './primitives/ApiExplorerJsonField';
import { ApiExplorerStringField } from './primitives/ApiExplorerStringField';
import { ApiExplorerFieldProps } from './props';

interface Props extends ApiExplorerFieldProps {}

// Custom attributes value field
const ApiExplorerAnyField: FC<Props> = ({ currentValue, onChange, path }) => {
  const [primitiveType, primitiveValue] = useMemo(
    () => transformValueToPrimitive(currentValue),
    [currentValue]
  );

  const [dataType, setDataTypeValue] = useState<
    AnyFieldOptionsType | undefined
  >(primitiveType as AnyFieldOptionsType);

  const onDataTypeChange = useCallback(
    (value: AnyFieldOptionsType) => {
      // Reset the user input when the data type changes
      const newValue = value === AnyFieldOptions.NULL ? null : undefined;
      onChange(newValue, path);
      setDataTypeValue(value);
    },
    [onChange, path]
  );

  const comboboxValues = useMemo(
    () =>
      customAttributeTypes.values.map((type) => ({
        value: type.text,
        displayText: type.text,
        description: type.subtext,
      })),
    []
  );

  const renderAnyField = () => {
    if (dataType === AnyFieldOptions.BOOL) {
      return (
        <ApiExplorerBooleanField
          currentValue={primitiveValue}
          onChange={onChange}
          path={path}
          name={'Select one'}
        />
      );
    }
    if (dataType === AnyFieldOptions.STRING) {
      return (
        <ApiExplorerStringField
          currentValue={primitiveValue}
          onChange={onChange}
          path={path}
          type={toIgnitionDataType('string')}
          name={'string'}
        />
      );
    }
    if (dataType === AnyFieldOptions.NUM || dataType === AnyFieldOptions.INT) {
      return (
        <ApiExplorerStringField
          currentValue={primitiveValue}
          onChange={onChange}
          path={path}
          type={toIgnitionDataType('number')}
          name={'number'}
        />
      );
    }
    if (dataType === AnyFieldOptions.OBJ || dataType === AnyFieldOptions.ARR) {
      return (
        <ApiExplorerJsonField
          currentValue={primitiveValue}
          name="any-json"
          onChange={onChange}
          path={path}
          type={toIgnitionDataType(
            dataType.toLowerCase() as 'object' | 'array'
          )}
        />
      );
    }
    return null;
  };

  return (
    <>
      <Combobox
        value={dataType}
        placeholder={'data type'}
        name={'any-data-type'}
        onChange={onDataTypeChange}
        comboboxValues={comboboxValues}
        readOnly={true}
      />
      <Box margin={{ top: '1x' }}>{renderAnyField()}</Box>
    </>
  );
};

export { ApiExplorerAnyField };
